import { Box, Text, Button, Flex, Spinner, Link } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { SubscriptionSelector } from "./components/SubscriptionSelector";
import { PaymentTitle } from "./components/PaymentTitle";
import { PaymentValueProps } from "./components/PaymentValueProps";
import { PaymentEyebrow } from "./components/PaymentEyebrow";
import { CloseIcon } from "@chakra-ui/icons";
import { t } from "i18next";
import { useAccountData } from "../../hooks/useAccountData";
import { ReaderLockedPages } from "./components/LockedPages";
import {
  GET_SUBSCRIPTION_PRICING_QUERY_KEY,
  GET_GIFTCARD_PRICING_QUERY_KEY,
  getSubscriptionPricing,
  getGiftCardPricing,
  webPurchase,
} from "./api/paymentApi";
import {
  StripePayment,
  StripePaymentConfigTypes,
} from "./components/StripePayment";
import { PromoCodeInput } from "./components/PromoCodeInput";
import { FunnelComponentProps, FunnelContextArea } from "../funnel/types";
import axios, { AxiosResponse } from "axios";
import { Release, ServerResponse } from "../../types";
import { useUserData } from "../../hooks/useUserData";
import { FUNNEL_TYPES } from "../funnel/utils/contants";
import { SubscriptionPriceTypes } from "./types";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { usePaymentMutation } from "./hooks/usePaymentMutation";
import { EventTypes } from "../../screens/root/api/eventsApi";
import { useIsVisible } from "../../hooks/useIsVisible";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsSmallWindowHeight } from "../../utils/useIsSmallWindowHeight";
import { useSendPurchaseEvent } from "../../hooks/useSendPurchaseEvent";
import { AuthFunnel } from "../auth/components/AuthFunnel";
import { pendingAuthPaymentConfigStore } from "../../hookstate/pendingAuthPaymentConfigStore";
import { CaretLeftLargeIcon } from "../icons/CaretLeftLargeIcon";
import { rootComponentsStore } from "../../hookstate/rootComponentsStore";
import { useRefetchAccountAndRelease } from "../../hooks/useRefetchAccountAndRelease";
import { FunnelSteps } from "../funnel/FunnelSteps";

export function SubscriptionModalHeader({
  release,
  modalContext,
  setFunnelType,
  onClose,
}: FunnelComponentProps) {
  const { userData } = useUserData();
  const { ppb_gold_price_point, premium_only } = release ?? {};

  const isGoldUser = userData?.gold ?? false;

  const isContextWithNoX = modalContext === FunnelContextArea.verticalReader;
  const isContextWithPrePaywall =
    modalContext === FunnelContextArea.reader ||
    modalContext === FunnelContextArea.verticalReader;

  const hasBackButton =
    isContextWithPrePaywall &&
    !isGoldUser &&
    (!premium_only || ppb_gold_price_point);

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      flexDirection="row"
      width="100%"
      padding="24px 24px"
    >
      <Box>
        {hasBackButton && (
          <Button
            variant="iconOnly"
            onClick={() => setFunnelType(FUNNEL_TYPES.prePaywall)}
            minWidth={0}
          >
            <CaretLeftLargeIcon color="dune.400" boxSize="20px" />
          </Button>
        )}
      </Box>
      {isContextWithNoX ? null : (
        <Button
          gc-testing-id="btnCloseSubscriptionModal"
          variant="iconOnly"
          onClick={onClose}
          minWidth={0}
        >
          <CloseIcon color="dune.100" />
        </Button>
      )}
    </Flex>
  );
}

export const SubscriptionModalBody = memo(
  ({
    modalContext = FunnelContextArea.default,
    onSuccess: onFunnelSuccess,
    onClose,
    sendEvent,
    hideValueProps = false,
    hidePromoCode = false,
    triggerFocusPayment = 0,
    showEmailPasswordForm = false,
    centerWelcomeText = false,
    largeLoginCTA = false,
    shouldShowSteps,
    stepCount,
    currentStep,
    stepText,
    stepDisplayText,
    showStepIndicators,
    updateFunnelConfig,
    displayExploreMoreContent,
    initialPromoCode,
    customHeader,
    customSubmitButtonText,
    showYearlyOnly = false,
    hideHeader = false,
    removeBackground = false,
    fullWidth = false,
  }: FunnelComponentProps & {
    context?: string;
    customHeader?: string | JSX.Element;
    customSubmitButtonText?: string;
    displayExploreMoreContent?: boolean;
    hideValueProps?: boolean;
    hidePromoCode?: boolean;
    release?: Release;
    centerWelcomeText?: boolean;
    showEmailPasswordForm?: boolean;
    triggerFocusPayment?: number;
    largeLoginCTA?: boolean;
    initialPromoCode?: string;
    showYearlyOnly?: boolean;
    hideHeader?: boolean;
    removeBackground?: boolean;
    fullWidth?: boolean;
  }) => {
    const accountData = useAccountData();
    const { userData } = useUserData();
    const { sendPurchaseEvent } = useSendPurchaseEvent();
    const [urlSearchParams] = useSearchParams();
    const [promoCode, setPromoCode] = useState<string>(
      initialPromoCode ?? urlSearchParams.get("code") ?? "",
    );
    const [appliedPromoCode, setAppliedPromoCode] = useState<string>(
      initialPromoCode ?? urlSearchParams.get("code") ?? "",
    );
    const [activeType, setActiveType] = useState<"yearly" | "monthly">();
    const [activeAuthType, setActiveAuthType] = useState<FUNNEL_TYPES>();
    const refetchQueries = useRefetchAccountAndRelease();
    const isSmallBreakpoint = useIsSmallBreakpoint();
    const isSmallWindowHeight = useIsSmallWindowHeight();

    const useFixedFooter = isSmallBreakpoint;

    const [yearlySub, setYearlySub] = useState<SubscriptionPriceTypes>();
    const [monthlySub, setMonthlySub] = useState<SubscriptionPriceTypes>();
    const navigate = useNavigate();

    const isReaderContext =
      modalContext === FunnelContextArea.reader ||
      modalContext === FunnelContextArea.verticalReader;
    const isGiftContext = modalContext === FunnelContextArea.gift;
    const isGoldContext =
      modalContext === FunnelContextArea.gold || isGiftContext;
    const showSmallContent =
      isSmallBreakpoint || isGoldContext || isSmallWindowHeight;
    const goldLarge = isGoldContext && !isSmallBreakpoint;
    const selectedQueryKey = isGiftContext
      ? [...GET_GIFTCARD_PRICING_QUERY_KEY, appliedPromoCode]
      : [...GET_SUBSCRIPTION_PRICING_QUERY_KEY, appliedPromoCode];

    const getPurchasePayload = useCallback(
      (stripePaymentConfig: StripePaymentConfigTypes) => {
        const { access_token_type_id, current_price, promo_code } =
          (activeType === "yearly" ? yearlySub : monthlySub) ?? {};
        return {
          ...stripePaymentConfig,
          accessTokenTypeId: access_token_type_id ?? 0,
          totalMinorUnits: Math.round((current_price ?? 0) * 100),
          incentiveCode: promoCode.length ? promoCode : promo_code,
        };
      },
      [activeType, yearlySub, monthlySub, promoCode],
    );

    type PurchaseMeta = {
      congrats_image?: string;
      congrats_text?: string;
      shelf_cta?: boolean;
    };

    const onSuccess = async (
      response?: AxiosResponse<ServerResponse<unknown, PurchaseMeta>>,
    ) => {
      setWaiting(true);

      const purchasedSubscription =
        activeType === "yearly" ? yearlySub : monthlySub;
      if (purchasedSubscription) {
        sendPurchaseEvent({
          value: purchasedSubscription.current_price,
          currency: purchasedSubscription.currency_code,
          itemId: purchasedSubscription.access_token_type_id,
          itemName: `${purchasedSubscription.billing_frequency} subscription`,
        });
      }

      // Extract congrats config from API response meta if available
      const meta = response?.data?.meta;
      const congratsConfig = meta
        ? {
            customCongratsImage: meta.congrats_image,
            customCongratsText: meta.congrats_text,
          }
        : undefined;

      const data = await refetchQueries();
      onFunnelSuccess?.({
        data,
        options: {
          didPurchaseSubscription: true,
          purchasedYearly: activeType === "yearly",
          componentProps: congratsConfig,
        },
      });
    };

    const onError = (error: Error) => {
      setActiveAuthType(undefined);
      let errorMessage = t(
        "components.payments.subscription.subscriptionDeclined",
      );

      if (axios.isAxiosError<ServerResponse<string>>(error)) {
        errorMessage = error.response?.data?.payload.results || errorMessage;
      }
      return errorMessage;
    };

    const {
      mutate,
      waiting,
      setWaiting,
      failedCard,
      cardValidationError,
      setCardValidationError,
    } = usePaymentMutation(webPurchase, onSuccess, onError);

    const { data } = useQuery({
      enabled: !waiting && !!accountData,
      queryKey: selectedQueryKey,
      queryFn: isGiftContext
        ? () => getGiftCardPricing(appliedPromoCode)
        : () => getSubscriptionPricing(appliedPromoCode),
      refetchOnWindowFocus: false,
    });

    useEffect(() => {
      const results = data?.data.payload.results;
      const subscriptions = results?.subscriptions;

      if (!subscriptions) return;

      setYearlySub(
        subscriptions.find((sub) => sub.billing_frequency === "yearly"),
      );
      setMonthlySub(
        subscriptions.find((sub) => sub.billing_frequency === "monthly"),
      );
    }, [data]);

    useEffect(() => {
      if (!activeType || !appliedPromoCode) {
        if (yearlySub?.promo_code) {
          setActiveType("yearly");
        } else if (monthlySub?.promo_code) {
          setActiveType("monthly");
        } else {
          setActiveType("yearly");
        }
      } else {
        if (appliedPromoCode === yearlySub?.promo_code) {
          setActiveType("yearly");
        } else if (appliedPromoCode == monthlySub?.promo_code) {
          setActiveType("monthly");
        }
      }
    }, [yearlySub, monthlySub]);

    const ref = useRef<HTMLDivElement>(null);
    const isVisible = useIsVisible(ref, 0.5);
    const alreadyTracked = useRef(false);

    useEffect(() => {
      if (sendEvent && isVisible && !alreadyTracked.current) {
        sendEvent(EventTypes.viewed_subscription_offer);
        alreadyTracked.current = true;
      }
    }, [isVisible]);

    // Default to using "modal" subscription context unless otherwise defined
    const contextMap = {
      [FunnelContextArea.reader]: "reader",
      [FunnelContextArea.verticalReader]: "reader",
    };
    const getActivePaywall = () => {
      let activePaywalls =
        activeType === yearlySub?.billing_frequency
          ? yearlySub?.paywalls
          : monthlySub?.paywalls;

      if (modalContext === FunnelContextArea.offerPopUp) {
        activePaywalls = activePaywalls?.filter((paywall) => paywall.is_intro);
      }

      const activeContext =
        contextMap[modalContext as keyof typeof contextMap] ?? "modal";
      return activePaywalls?.find(
        (paywall) => paywall.context === activeContext,
      );
    };

    const [isPromoValid, setIsPromoValid] = useState(true);
    useEffect(() => {
      if ((!promoCode && !appliedPromoCode) || appliedPromoCode === promoCode) {
        setIsPromoValid(true);
        setCardValidationError(undefined);
        return;
      }
      const timeout = setTimeout(() => {
        setIsPromoValid(false);
        setCardValidationError(
          t("components.payments.subscription.applyPromoToComplete"),
        );
      }, 400);
      return () => clearTimeout(timeout);
    }, [promoCode, appliedPromoCode]);

    // Either send the purchase to the API or save the purchase payload
    // for after auth
    const submitPurchase = useCallback(
      (stripePaymentConfig: StripePaymentConfigTypes) => {
        if (!userData) {
          pendingAuthPaymentConfigStore.set(
            getPurchasePayload(stripePaymentConfig),
          );
          setActiveAuthType(FUNNEL_TYPES.signup);
        } else {
          mutate(getPurchasePayload(stripePaymentConfig));
        }
      },
      [getPurchasePayload, mutate, userData],
    );

    // Determine if we need to use the pending payment config
    // If the user is gold we don't need to use the pending config
    // If the user is not gold we send the pending config to the api
    // and purchase.
    useEffect(() => {
      const pendingConfig = pendingAuthPaymentConfigStore.get({
        noproxy: true,
      });
      if (userData?.gold && pendingConfig) {
        pendingAuthPaymentConfigStore.set(undefined);

        onSuccess();
      } else if (pendingConfig) {
        mutate(pendingConfig);
        pendingAuthPaymentConfigStore.set(undefined);
      }
    }, [userData]);

    // Clear the pending config on unmount
    useEffect(() => {
      return () => {
        pendingAuthPaymentConfigStore.set(undefined);
      };
    }, []);

    const activePaywall = getActivePaywall();

    if (!data || !yearlySub || !monthlySub || !activePaywall) {
      return (
        <Flex
          width="100%"
          justifyContent="center"
          alignItems="center"
          height="531px"
          padding="24px"
          ref={ref}
        >
          <Spinner color="blaze.blaze" />
        </Flex>
      );
    }

    return (
      <Flex
        flexDirection={goldLarge ? "row" : "column"}
        gap={showSmallContent ? "16px" : "24px"}
        alignItems="center"
        justifyContent={goldLarge ? "space-evenly" : "center"}
        borderRadius="16px"
        padding="24px"
        ref={ref}
      >
        {shouldShowSteps ? (
          <FunnelSteps
            stepCount={stepCount}
            currentStep={currentStep}
            stepText={stepText}
            text={stepDisplayText}
            hideProgressIndicators={!showStepIndicators}
          />
        ) : (activeAuthType && !isGoldContext) ||
        hideHeader ? null : (
          <Flex
            flexDirection="column"
            width="fit-content"
            gap={isGoldContext ? "32px" : "24px"}
            alignItems={goldLarge ? "flex-start" : "center"}
            paddingBottom={isGoldContext ? "24px" : undefined}
          >
            {isReaderContext && <ReaderLockedPages />}
            {activePaywall?.eyebrow && (
              <PaymentEyebrow
                alt={isGoldContext}
                text={activePaywall.eyebrow}
              />
            )}
            <PaymentTitle
              size={goldLarge ? "40px" : undefined}
              textAlign={goldLarge ? "left" : "center"}
              text={
                customHeader ||
                (activeAuthType && !isGoldContext
                  ? ""
                  : (activePaywall?.main_title ?? ""))
              }
            />
            {!hideValueProps && (
              <PaymentValueProps
                showSeparators={false}
                alignItems="start"
                valueProps={activePaywall?.value_props ?? ["", "", ""]}
                iconColor={isGoldContext ? "dune.dune" : undefined}
              />
            )}
          </Flex>
        )}
        <Flex
          flexDirection="column"
          maxWidth={fullWidth ? "none" : goldLarge ? "500px" : "100%"}
          width="100%"
          gap={isSmallBreakpoint || isGoldContext ? "16px" : "24px"}
          padding={
            removeBackground ? undefined : isGoldContext ? "24px" : undefined
          }
          alignItems={isGoldContext ? "flex-start" : "center"}
          background={
            removeBackground
              ? "transparent"
              : isGoldContext
                ? "linear-gradient(346deg, rgba(20, 21, 23, 0.75) 66.94%, rgba(68, 90, 119, 0.11) 200.5%);"
                : undefined
          }
          borderColor={
            removeBackground
              ? undefined
              : isGoldContext
                ? "1px solid var(--White-Transparency-White-10, rgba(255, 255, 255, 0.10));"
                : undefined
          }
          backdropFilter={
            removeBackground
              ? undefined
              : isGoldContext
                ? "blur(18px)"
                : undefined
          }
          boxShadow={
            removeBackground
              ? undefined
              : isGoldContext
                ? "0px 4px 24px 0px rgba(0, 0, 0, 0.40)"
                : undefined
          }
          borderRadius={
            removeBackground ? undefined : isGoldContext ? "16px" : undefined
          }
        >
          {!activeAuthType ? (
            <>
              <Flex
                flexDirection="column"
                width="100%"
                gap="16px"
                paddingTop="14px"
              >
                <SubscriptionSelector
                  isSmall={showSmallContent}
                  onClick={setActiveType}
                  subscriptionPrice={yearlySub}
                  active={yearlySub?.billing_frequency === activeType}
                />
                {!showYearlyOnly && (
                  <SubscriptionSelector
                    isSmall={showSmallContent}
                    onClick={setActiveType}
                    subscriptionPrice={monthlySub}
                    active={monthlySub?.billing_frequency === activeType}
                  />
                )}
                <Text
                  color="neutral.300"
                  fontSize="12px"
                  lineHeight="150%"
                  textAlign="center"
                >
                  {t(
                    isGiftContext
                      ? "components.payments.subscription.giftDelivery"
                      : "components.payments.subscription.cancelAnytimeAdditionalPurchase",
                  )}
                </Text>
              </Flex>
              {hidePromoCode ? null : (
                <PromoCodeInput
                  isSmall={showSmallContent}
                  promoApplied={appliedPromoCode}
                  isValid={
                    appliedPromoCode === yearlySub?.promo_code ||
                    appliedPromoCode === monthlySub?.promo_code
                  }
                  applyPromoCode={setAppliedPromoCode}
                  onChange={setPromoCode}
                />
              )}
              <StripePayment
                triggerFocusPayment={triggerFocusPayment}
                isSmall={showSmallContent}
                waiting={waiting}
                isDisabled={!isPromoValid}
                responseError={cardValidationError}
                failedCard={failedCard}
                useFixedFooter={useFixedFooter}
                submitButtonText={
                  customSubmitButtonText ||
                  (userData
                    ? t(
                        isGiftContext
                          ? "components.payments.subscription.confirmGiftCardPayment"
                          : "components.payments.subscription.confirmYourPayment",
                      )
                    : t(
                        "components.payments.subscription.subscribeToGlobalComixGold",
                      ))
                }
                completePurchase={submitPurchase}
                isGiftCard={isGiftContext}
              />
              {displayExploreMoreContent && (
                <Link
                  gc-testing-id="btnExploreMoreContent"
                  fontSize="14px"
                  lineHeight="130%"
                  color="neutral.300"
                  fontWeight="500"
                  textDecorationLine="underline"
                  alignSelf="center"
                  onClick={() => {
                    navigate("/");
                    onClose?.();
                    rootComponentsStore.openSlideInMenu.set(undefined);
                    rootComponentsStore.slideInMenuConfig.set({});
                  }}
                >
                  {t("components.payments.subscription.exploreMoreContent")}
                </Link>
              )}
            </>
          ) : (
            <AuthFunnel
              activeAuthType={activeAuthType}
              setFunnelType={setActiveAuthType}
              updateFunnelConfig={updateFunnelConfig}
              onClose={onClose}
              onSuccess={() => {
                setWaiting(true);
              }}
              waiting={waiting}
              sendEvent={sendEvent}
              centerWelcomeText={centerWelcomeText}
              showEmailPasswordForm={showEmailPasswordForm}
              largeLoginCTA={largeLoginCTA}
            />
          )}
        </Flex>
      </Flex>
    );
  },
);
